import { Box, Typography } from "@mui/material";
import type { GlanceField } from "../campaignTypes";
import {
  PORTAL_MAX_WIDTH,
  bodySx,
  cardSurfaceSx,
  eyebrowSx,
  portalFont,
  sectionTitleSx,
} from "../partnerTheme";

export default function CampaignOverviewGrid({
  fields,
  eyebrow,
  title,
}: {
  fields: GlanceField[];
  eyebrow?: string;
  title?: string;
}) {
  if (!fields.length) return null;

  const sectionEyebrow = eyebrow === undefined ? "The setup" : eyebrow;
  const sectionTitle = title === undefined ? "Campaign at a glance" : title;
  const columnsMd = fields.length % 4 === 0 ? 4 : 3;

  return (
    <Box
      id="overview"
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        pt: { xs: 7, md: 10 },
        pb: { xs: 10, md: 16 },
        scrollMarginTop: 96,
      }}
    >
      {sectionEyebrow ? (
        <Typography sx={{ ...eyebrowSx, fontSize: { xs: "0.75rem", md: "0.8125rem" } }}>
          {sectionEyebrow}
        </Typography>
      ) : null}
      {sectionTitle ? (
        <Typography
          sx={{
            ...sectionTitleSx,
            fontSize: { xs: "1.85rem", md: "2.75rem" },
            mb: { xs: 3, md: 4 },
            mt: sectionEyebrow ? 0 : undefined,
          }}
        >
          {sectionTitle}
        </Typography>
      ) : null}
      <Box
        sx={{
          ...cardSurfaceSx,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: `repeat(${columnsMd}, 1fr)`,
          },
          overflow: "hidden",
        }}
      >
        {fields.map((field, i) => (
          <Box
            key={field.id}
            sx={{
              p: { xs: 2.25, sm: 3, md: 4.5 },
              minHeight: { md: 148 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderRight: {
                xs: "none",
                sm: i % 2 === 0 ? "1px solid var(--portal-border)" : "none",
                md:
                  (i + 1) % columnsMd === 0
                    ? "none"
                    : "1px solid var(--portal-border)",
              },
              borderBottom: {
                xs: i < fields.length - 1 ? "1px solid var(--portal-border)" : "none",
                sm:
                  i < fields.length - (fields.length % 2 === 0 ? 2 : 1)
                    ? "1px solid var(--portal-border)"
                    : "none",
                md:
                  i < fields.length - (fields.length % columnsMd || columnsMd)
                    ? "1px solid var(--portal-border)"
                    : "none",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: { xs: "0.6875rem", md: "0.75rem" },
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--portal-text-muted)",
                mb: { xs: 0.75, md: 1.25 },
              }}
            >
              {field.label}
            </Typography>
            <Typography
              sx={{
                ...bodySx,
                color: "var(--portal-text)",
                fontSize: { xs: "1rem", md: "1.25rem" },
                fontWeight: 600,
                lineHeight: 1.4,
                overflowWrap: "anywhere",
              }}
            >
              {field.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
